package com.steamstreet.vegasful.browser.public

import com.steamstreet.vegasful.web.analytics.Analytics
import com.steamstreet.vegasful.web.analytics.AnalyticsEvent
import com.steamstreet.vegasful.web.analytics.AnalyticsParam
import com.steamstreet.vegasful.web.analytics.pageTypeOf
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.asList
import org.w3c.dom.events.Event as DomEvent

fun Element.appAttribute(key: String): String? {
    return this.getAttribute("data-vegasful-${key}")
}

/**
 * Client-side tag filtering for content-view event panels.
 *
 * Each filter group is a configured parent tag whose child tags are the popup
 * options rendered above the events. Selecting options filters the visible
 * events in real time. An event is shown when, for every group that has an
 * active selection, the event carries at least one of that group's selected
 * tags — OR within a group, AND across groups. An empty selection ("All")
 * imposes no constraint for that group.
 *
 * The server renders the DOM contract this reads: `[data-filter-group]` groups
 * each containing a `.event-tag-filter-trigger` and `a[data-filter-value]`
 * options (empty value == "All"), plus the events, which carry
 * `data-vegasful-entity-type=event` and a comma-separated `data-vegasful-tags`.
 */
fun eventTagFilter(element: Element) {
    // group id -> selected tag ids. An empty set means the group is on "All".
    val selections = mutableMapOf<String, MutableSet<String>>()

    val eventElements = element.querySelectorAll("[data-vegasful-entity-type=event]")
        .asList().filterIsInstance<HTMLElement>()

    // Freeze the current height so hiding events doesn't collapse the layout as
    // the reader filters.
    val initialHeight = window.getComputedStyle(element).height
    element.asDynamic().style.minHeight = initialHeight

    fun eventTags(el: Element): List<String> =
        el.appAttribute("tags")?.split(",")?.filter { it.isNotEmpty() }.orEmpty()

    fun isIncluded(el: Element): Boolean {
        val tags = eventTags(el)
        return selections.values.all { selected ->
            selected.isEmpty() || tags.any { it in selected }
        }
    }

    fun hasIncludedChild(container: Element): Boolean =
        container.querySelectorAll("[data-vegasful-excluded=include]").asList().isNotEmpty()

    // Collapse day/venue groupings that no longer contain any visible event.
    fun refreshContainers(selector: String) {
        element.querySelectorAll(selector).asList().forEach { node ->
            val container = node as Element
            container.setAttribute(
                "data-vegasful-excluded",
                if (hasIncludedChild(container)) "include" else "exclude"
            )
        }
    }

    fun applyFilter() {
        eventElements.forEach { el ->
            el.setAttribute("data-vegasful-excluded", if (isIncluded(el)) "include" else "exclude")
        }
        refreshContainers("[data-vegasful-date-container]")
        refreshContainers("[data-vegasful-entity-type=venue]")
    }

    fun updateSelectionState(group: Element) {
        val groupId = group.getAttribute("data-filter-group") ?: return
        val selected = selections[groupId].orEmpty()
        group.querySelectorAll("[data-filter-value]").asList().forEach { node ->
            val option = node as Element
            val value = option.getAttribute("data-filter-value").orEmpty()
            val isSelected = if (value.isEmpty()) selected.isEmpty() else value in selected
            if (isSelected) {
                option.setAttribute("data-selected", "1")
            } else {
                option.removeAttribute("data-selected")
            }
        }
        val trigger = group.querySelector(".event-tag-filter-trigger")
        if (selected.isEmpty()) {
            trigger?.removeAttribute("data-selected")
        } else {
            trigger?.setAttribute("data-selected", "1")
        }
    }

    fun closeAllMenus() {
        element.querySelectorAll("[data-filter-group][data-open=\"1\"]").asList().forEach {
            (it as Element).removeAttribute("data-open")
        }
    }

    // Each trigger opens its own popup and closes the others.
    element.querySelectorAll(".event-tag-filter-group").asList().forEach { node ->
        val group = node as Element
        group.querySelector(".event-tag-filter-trigger")?.addEventListener("click", { event: DomEvent ->
            event.stopPropagation()
            val isOpen = group.getAttribute("data-open") == "1"
            closeAllMenus()
            if (!isOpen) {
                group.setAttribute("data-open", "1")
            }
        })
        updateSelectionState(group)
    }

    // Each option toggles its membership in the group's selection (multi-select),
    // except "All" (empty value), which clears the group.
    element.querySelectorAll("[data-filter-value]").asList().forEach { node ->
        val option = node as Element
        option.addEventListener("click", { event: DomEvent ->
            event.preventDefault()
            event.stopPropagation()
            val group = option.closest("[data-filter-group]")
            val groupId = group?.getAttribute("data-filter-group")
            if (group != null && groupId != null) {
                val value = option.getAttribute("data-filter-value").orEmpty()
                val selected = selections.getOrPut(groupId) { mutableSetOf() }
                when {
                    value.isEmpty() -> selected.clear()
                    value in selected -> selected.remove(value)
                    else -> selected.add(value)
                }
                updateSelectionState(group)
                applyFilter()

                // Reported after the filter has run, so `results_count` is what the reader can
                // now see rather than what they could see a moment ago. A selection cleared back
                // to "All" reports a count of zero, which is how "people undo this filter" shows
                // up at all.
                Analytics.track(
                    AnalyticsEvent.FILTER_APPLIED,
                    AnalyticsParam.FILTER_TYPE to groupId,
                    AnalyticsParam.SELECTED_COUNT to selected.size,
                    AnalyticsParam.RESULTS_COUNT to eventElements.count { isIncluded(it) },
                    AnalyticsParam.PAGE_TYPE to pageTypeOf(window.location.pathname)
                )
            }
        })
    }

    // A click anywhere else dismisses any open popup.
    document.addEventListener("click", { event: DomEvent ->
        val target = event.target as? Element
        if (target == null || target.closest("[data-filter-group]") == null) {
            closeAllMenus()
        }
    })
}
